import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import { getUser, updateUser } from '../services/usersService';
import { FormattedMessage } from 'react-intl';
import Dialog from '../../../components/Dialog';
import UserForm from './UserForm';
import { Stack, CircularProgress, Button, Grid } from '@mui/material';
import Iconify from '../../../components/Iconify';

// ----------------------------------------------------------------------

export default function EditUserDialog({ id, onClose, ...dialogProps }) {
  const [data, setData] = useState({ isFetching: true });
  const [editOn, setEditOn] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const handleUserUpdate = async (values, actions) => {
    try {
      await updateUser(id, values);
      enqueueSnackbar(<FormattedMessage id='user.update.msg.success' defaultMessage='User successfully updated' />, {
        variant: 'success',
      });
      onClose(true);
    } catch (error) {
      enqueueSnackbar(<FormattedMessage id='user.update.msg.error' defaultMessage='User update failed' />, {
        variant: 'error',
      });
      actions.setSubmitting(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const user = await getUser(id);
        enqueueSnackbar(<FormattedMessage id='user.fetch.msg.success' defaultMessage='User successfully fetched' />, {
          variant: 'success',
        });
        setData({ isFetching: false, user });
      } catch (error) {
        enqueueSnackbar(<FormattedMessage id='user.fetch.msg.error' defaultMessage='User fetch failed' />, {
          variant: 'error',
        });
        setData({ isFetching: false });
      }
    };

    fetchData();
  }, []);

  const handleCancel = () => {
    setEditOn(false);
  };

  const handleClose = () => {
    onClose(false);
  };

  return (
    <Dialog
      {...dialogProps}
      onClose={handleClose}
      title={<FormattedMessage id='user.update.title' defaultMessage='User' />}
    >
      {
        data.isFetching ?
          <Stack alignItems="center">
            <CircularProgress />
          </Stack> :
          <>
            {
              !editOn &&
              <Grid container justifyContent="flex-end" paddingBottom={2}>
                <Button
                  variant="contained"
                  onClick={() => setEditOn(true)}
                  startIcon={<Iconify icon="heroicons-solid:pencil"
                  />}
                >
                  <FormattedMessage id='user.button.edit' defaultMessage='Edit user' />
                </Button>
              </Grid>
            }
            <UserForm
              user={data.user}
              onSubmit={handleUserUpdate}
              editOn={editOn}
              onCancel={handleCancel}
              changableLocations={true}
            />
          </>

      }
    </Dialog>
  );
}

EditUserDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  id: PropTypes.number.isRequired
};
